import { PageLoading } from "@ant-design/pro-components";
import { message } from "antd";
import { createContext, FC, ReactNode, useContext, useReducer } from "react";
import auth from "services/admin/auth";
import reference from "services/guest/reference";
import useRequest from "utils/useRequest";
import { setCurrencyMasked } from "utils";
import { Action, DataType, ReducerType, Refrences, Type } from "./types";

const DEFAULT_REFERENCE: Refrences = {
  loading: true,
};

const DEFAULT_DATA: DataType = {
  authorized: false,
  settings: {
    layout: "side",
    navTheme: "light",
    colorWeak: false,
    title: "VMS",
    fixedHeader: true,
    fixSiderbar: true,
    contentWidth: "Fluid",
    listen_socket: false,
  },
  refrences: DEFAULT_REFERENCE,
  langs: [],
  notificationsCount: 0,
  maskCurrency: auth.getMaskCurrency(),
};

const reducer: ReducerType = (state, action) => {
  switch (action[0]) {
    case Action.SET_SETTINGS:
      return { ...state, settings: action[1] };
    case Action.SIGN_IN:
      return { ...state, auth: action[1], authorized: true };
    case Action.SET_REFRENCES:
      return { ...state, refrences: action[1] };
    case Action.INCREASE_NOTI_COUNT:
      return { ...state, notificationsCount: state.notificationsCount + 1 };
    case Action.SET_LANGS:
      return { ...state, langs: action[1] };
    case Action.SIGN_OUT:
      auth.remToken();
      return { ...state, auth: undefined, authorized: false };
    case Action.MASK_CURRENCY:
      auth.setMaskCurrency(action[1]);
      setCurrencyMasked(action[1]);
      return { ...state, maskCurrency: action[1] };
    default:
      return state;
  }
};

const StoreContext = createContext<Type>([
  DEFAULT_DATA,
  () => undefined,
  {
    info: () => undefined,
  },
]);

export const StoreProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, DEFAULT_DATA);

  const { loading: authLoading, refresh: refreshAuth } = useRequest(auth.info, {
    onSuccess: (data) => dispatch([Action.SIGN_IN, data]),
    ready: auth.hasToken(),
  });

  const { loading: langLoading } = useRequest(reference.listLang, {
    onSuccess: (data) => dispatch([Action.SET_LANGS, data || []]),
    onError: (error) => message.error(error.message),
  });

  const isLoading = authLoading || langLoading;

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <StoreContext.Provider value={[state, dispatch, { info: refreshAuth }]}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): Type => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
};
